<template>
  <div
    :class="{
      'fact-finder-filter-fi': true,
      'fact-finder-filter-fi--disabled': filter.disabled,
      // Three-way, and none of it breakpoint-derived: --open and --collapsed are the user's
      // explicit choices, --default-open marks the groups CSS opens on its own at md. The
      // default has to live in CSS, since the server has no viewport and would otherwise render
      // every group collapsed and reopen the first two after mount.
      'fact-finder-filter-fi--open': optionsState === 'open',
      'fact-finder-filter-fi--collapsed': optionsState === 'collapsed',
      'fact-finder-filter-fi--default-open': defaultOpen,
      'fact-finder-filter-fi--active': activeFilter,
      'fact-finder-filter-fi--filters-active': activeFilters,
    }"
  >
    <div
      class="fact-finder-filter-fi__name"
      @click="toggleFacet"
    >
      <div class="fact-finder-filter-fi__name__text">
        {{ cleanName(filter.name || '') }}

        <div
          v-if="activeFilter"
          class="fact-finder-filter-fi__name__active-facets"
        >
          {{ activeFiltersText }}
        </div>
      </div>

      <icon-fi
        v-if="!filter.disabled"
        class="fact-finder-filter-fi__icon"
        icon="chevron-up"
      />
    </div>

    <div
      v-if="haveBeenShown || optionsState === 'open'"
      class="fact-finder-filter-fi__options"
    >
      <div class="fact-finder-filter-fi__options__inner">
        <div class="fact-finder-filter-fi__mobile-header">
          <div
            class="fact-finder-filter-fi__mobile-header__text"
            @click="optionsState = 'collapsed'"
          >
            <icon-fi
              icon="chevron-left"
              class="fi-flex-shrink-0"
            />
            <span class="fi-line-clamp-1 fi-text-ellipsis">{{ cleanName(filter.name || '') }}</span>
          </div>
        </div>

        <div class="fact-finder-filter-fi__filters">
          <fact-finder-text-filter-fi
            v-if="filter.type === 'TEXT'"
            :filter="filter"
            @filter-changed="filterChanged"
          />
          <fact-finder-range-filter-fi
            v-else-if="isRangeFilter"
            :filter="filter"
            @filter-changed="filterChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { computed, nextTick, ref, watch, watchEffect } from 'vue';

import type { ProcessedFactFinderNgFacet } from '@/components/facets/processedFacetTypes.ts';
import factFinderNgNameCleaner from '@/components/search-fact-finder-fi/factFinderNgNameCleaner';
import type { SearchFilter } from '@/components/search-fact-finder-results-fi/SearchFactFinderResultsFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';

import FactFinderRangeFilterFi from '../fact-finder-range-filter-fi/FactFinderRangeFilterFi.vue';
import FactFinderTextFilterFi from '../fact-finder-text-filter-fi/FactFinderTextFilterFi.vue';

defineOptions({
  name: 'FactFinderFilterFi',
});

const { isMD } = useBreakpoints();

const props = defineProps<{
  filter: ProcessedFactFinderNgFacet & { disabled?: boolean };
  hideFilters: boolean;
  activeFilters: boolean;
  loading: boolean;
  idx: number;
}>();

const emits = defineEmits<{
  'filter-changed': [filter: SearchFilter];
}>();

type OptionsState = 'untouched' | 'open' | 'collapsed';

const optionsState = ref<OptionsState>('untouched');

/** The first two groups are the ones CSS opens by itself at md. Index-based, so SSR-stable. */
const defaultOpen = computed(() => props.idx < 2);

const haveBeenShown = ref(defaultOpen.value);

const isOpen = computed(() =>
  optionsState.value === 'untouched'
    ? isMD.value && defaultOpen.value
    : optionsState.value === 'open',
);

const waitForPaint = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );

const toggleFacet = async () => {
  if (props.filter.disabled) {
    return;
  }
  if (!haveBeenShown.value && !isOpen.value) {
    haveBeenShown.value = true;
    await nextTick();
    await waitForPaint();
  }
  optionsState.value = isOpen.value ? 'collapsed' : 'open';
};

watch(
  () => props.hideFilters,
  (value) => {
    if (value) {
      // Closing the drawer drops the user's choices and hands the groups back to the breakpoint
      // default - collapsed on mobile, first two open at md - which is what the old
      // "set false, then let the isMD effect reopen the first two" pair added up to.
      optionsState.value = 'untouched';
    }
  },
);

watchEffect(() => {
  if (!props.loading && props.filter.disabled) {
    optionsState.value = 'collapsed';
  }
});

const isRangeFilter = computed(() =>
  props.filter.type ? ['FLOAT', 'INTEGER'].includes(props.filter.type) : false,
);

const cleanName = (name: string) => factFinderNgNameCleaner(name);

const filterChanged = (filter: any) => {
  emits('filter-changed', filter);

  if (!isMD.value) {
    optionsState.value = 'collapsed';
  }
};

const activeFilter = computed(() => props.filter.elements?.find((el: any) => el.selectedBool));

const activeFiltersText = computed(() => {
  const filters = props.filter.elements
    ?.filter((el: any) => el.selectedBool)
    ?.map((el: any) => `${el.text} ${props.filter.unit}`);

  return filters ? filters.join(', ') : '';
});
</script>

<style scoped src="./fact-finder-filter-fi" />
